import { Fragment } from 'react';
import ReportingLine from '../ReportingLine';

export default function EmploymentTab({ data, showOdoo = true, isMemberView = false }) {
  // Fields sourced from Odoo HR (read-only, synced). Suppressed org-wide when
  // the odoo_profile_fields flag is off.
  //
  // Marital status deliberately is NOT here: it is personal data, not
  // employment data, and it lives on the Personal tab where the person can
  // actually edit it. One field, one home.
  // Departments are NOT repeated as a row here: the Departments card below
  // lists them and carries the per-department manager badge, which a joined
  // string cannot. One fact, one place.
  const hr = showOdoo
    ? [
        ['Job title', data.odoo_job_title],
        ['Manager', data.odoo_manager_name],
        ['Company', data.odoo_company_name],
      ].filter(([, v]) => v != null && v !== '')
    : [];

  const hasHr = hr.length > 0;

  // `odoo_company_access` is absent entirely when the viewer may not see
  // sensitive HR fields (redactSensitiveHr DELETES the key), and null when the
  // person has no Odoo login at all. Those are different, and only the second
  // one earns an explanation on screen.
  const canSeeCompanies = showOdoo && 'odoo_company_access' in data;
  const companies = data.odoo_company_access;
  const showDepts = data.departments?.length > 0;
  // Overview is two columns. With an odd number of short cards the last one
  // would sit beside an empty cell (self: Employment + Company access +
  // Departments; member: Employment alone), so it spans both columns instead.
  const cardCount = 1 + (canSeeCompanies ? 1 : 0) + (showDepts ? 1 : 0);
  const oddLast = (n) => (cardCount % 2 === 1 && n === cardCount ? ' profile-info-card--span' : '');

  return (
    <div className="profile-card-grid profile-card-grid--overview">
      <section className={`profile-info-card${oddLast(1)}`}>
        <header className="profile-info-card__head">
          <h2 className="profile-info-card__title">Employment information</h2>
          {hasHr && <span className="profile-source-tag">Synced from Odoo</span>}
        </header>
        <dl className="profile-info-rows">
          <Fragment>
            <dt>Role</dt>
            <dd>{data.is_admin ? 'Administrator' : 'Employee'}</dd>
          </Fragment>
          {hr.map(([label, value]) => (
            <Fragment key={label}>
              <dt>{label}</dt>
              <dd>{value}</dd>
            </Fragment>
          ))}
        </dl>
        {showOdoo && !hasHr && (
          <p className="profile-muted profile-info-card__empty">
            No HR details yet — they appear once {isMemberView ? 'their' : 'your'} Odoo record syncs.
          </p>
        )}
      </section>

      {canSeeCompanies && (
        <section className={`profile-info-card${oddLast(2)}`}>
          <header className="profile-info-card__head">
            <h2 className="profile-info-card__title">Company access</h2>
          </header>
          {companies && companies.length > 0 ? (
            <ul className="profile-depts">
              {companies.map((c) => (
                <li
                  key={c.id}
                  className={c.name === data.odoo_company_name ? 'is-manager' : undefined}
                >
                  {c.name}
                  {c.name === data.odoo_company_name && (
                    <span className="profile-dept-mgr">Default</span>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            // NULL means no Odoo login (176 of 321 employees have one), which
            // is a different fact from "access to zero companies".
            <p className="profile-muted profile-info-card__empty">
              No Odoo login — single company access.
            </p>
          )}
        </section>
      )}

      {showDepts && (
        <section className={`profile-info-card${oddLast(cardCount)}`}>
          <header className="profile-info-card__head">
            <h2 className="profile-info-card__title">Departments</h2>
          </header>
          <ul className="profile-depts">
            {data.departments.map((d) => (
              <li key={d.id} className={d.is_manager ? 'is-manager' : undefined}>
                {d.name}
                {d.is_manager && <span className="profile-dept-mgr">Manager</span>}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Self view only. The endpoint behind this resolves the employee from
          the session and has no per-id variant, so it would return the VIEWER's
          own line on someone else's profile — which would be actively
          misleading. Hidden rather than wrong. */}
      {!isMemberView && <ReportingLine />}
    </div>
  );
}
